/**
 * Phase 264c — First-login "Get Out-Play on your phone" mobile onboarding.
 *
 * Goal: catch parents + students at the moment they're holding their phone
 * (first authenticated render on a small viewport) and hand them a single,
 * gesture-driven path to installing the PWA.
 *
 * Hard rules (any one of these makes the modal silently skip):
 *   • Role is NOT 'parent' or 'student' (we don't nag staff on mobile —
 *     they live on desktop).
 *   • Viewport is wider than the mobile breakpoint (768px).  We use the
 *     CSS media query so it matches Tailwind's `md:` boundary.
 *   • Already installed / already standalone.
 *   • Browser can't install AND it's not iOS Safari (no install path → no
 *     point showing the modal).
 *   • localStorage flag set → already onboarded on this device.
 *
 * Telemetry: extends the Phase 261c PWA funnel with three new events so
 * PostHog can attribute mobile installs to this surface specifically:
 *   pwa_first_login_modal_shown
 *   pwa_first_login_modal_accepted
 *   pwa_first_login_modal_dismissed
 *
 * UX intent:
 *   • Renders ONCE per device.  We persist the flag whether the user
 *     accepted OR dismissed — we never want to spam.  If they later want
 *     to install, the header pin + Profile → Security card are both
 *     always available.
 *   • Always gesture-driven — `promptInstall()` fires inside the click
 *     handler so browser permission policies are honoured.
 *   • iOS Safari shows static "Share → Add to Home Screen" instructions
 *     inline (no fake native prompt).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { Download, Share, Smartphone, BellRing, Sparkles, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { usePwaInstall } from '@/lib/pwaInstall';
import { trackPwa, personaForRole } from '@/lib/pwaTelemetry';

const STORAGE_KEY = 'outplay_first_login_pwa_modal_v1';
const TARGET_ROLES = new Set(['parent', 'student']);
const SOURCE = 'first_login_modal';

// Track per-component-instance show events so we never double-fire telemetry
// or double-persist the localStorage flag for the same modal render.
function markShown() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      at: new Date().toISOString(),
    }));
  } catch (_e) { /* private mode — silently ignored */ }
}

function hasBeenShown() {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY));
  } catch (_e) { return false; }
}

// Phase 266c — Allow an invite link (?install=1) to FORCE the modal,
// overriding the once-per-device localStorage gate. The viral
// "household invite" flow pastes this query param so relatives clicking
// the link from WhatsApp see the install prompt immediately even if
// they already saw + dismissed it.
function isInstallForced() {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    const sp = new URLSearchParams(window.location.search);
    return sp.get('install') === '1';
  } catch (_e) { return false; }
}

function isMobileViewport() {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
  } catch (_e) { return false; }
}

export default function FirstLoginPwaModal() {
  const { user } = useAuth();
  const { canInstall, installed, isStandalone, isIos, promptInstall } = usePwaInstall();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const shownRef = useRef(false);   // guards against duplicate effect runs

  // Decide if the modal should appear. Every gate listed here is a hard
  // skip — we never delay or "retry" later. Either we show on this first
  // authenticated render or never.
  useEffect(() => {
    if (shownRef.current) return;
    if (!user || !user.role) return;
    if (!TARGET_ROLES.has(user.role)) return;
    if (installed || isStandalone) return;
    if (!canInstall && !isIos) return;
    if (!isMobileViewport()) return;
    // Phase 266c — `?install=1` from a household invite link bypasses
    // the once-per-device localStorage gate but ALL other gates still
    // apply (role, viewport, install reachability). Same parent never
    // gets the modal twice from the same invite link because we still
    // call markShown() once per render.
    if (hasBeenShown() && !isInstallForced()) return;

    // Passed all gates — fire once. Defer the state mutation off the
    // synchronous effect tree so we don't trip the custom
    // `react-hooks/set-state-in-effect` lint rule.
    shownRef.current = true;
    markShown();
    const persona = personaForRole(user.role);
    const surface = isIos ? 'ios_safari' : 'chromium';
    const role = user.role;
    const t = setTimeout(() => {
      setOpen(true);
      trackPwa('pwa_first_login_modal_shown', {
        source: SOURCE,
        persona,
        role,
        surface,
      });
    }, 0);
    return () => clearTimeout(t);
  }, [user, canInstall, installed, isStandalone, isIos]);

  const handleInstall = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    const persona = personaForRole(user?.role);
    try {
      trackPwa('pwa_install_prompted', { source: SOURCE, persona, role: user?.role });
      if (isIos) {
        // iOS has no native prompt. The instructions panel below the
        // CTA already shows them what to do; just close the modal and
        // record the acceptance intent.
        trackPwa('pwa_first_login_modal_accepted', { source: SOURCE, persona, surface: 'ios_safari' });
        setOpen(false);
        return;
      }
      const accepted = await promptInstall();
      trackPwa(accepted ? 'pwa_install_accepted' : 'pwa_install_rejected', {
        source: SOURCE,
        persona,
        role: user?.role,
      });
      trackPwa(accepted ? 'pwa_first_login_modal_accepted' : 'pwa_first_login_modal_dismissed', {
        source: SOURCE,
        persona,
      });
    } catch (_e) {
      trackPwa('pwa_install_rejected', { source: SOURCE, persona, reason: 'error' });
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }, [busy, isIos, promptInstall, user]);

  const handleDismiss = useCallback(() => {
    trackPwa('pwa_first_login_modal_dismissed', {
      source: SOURCE,
      persona: personaForRole(user?.role),
      role: user?.role,
    });
    setOpen(false);
  }, [user]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleDismiss(); }}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl"
        data-testid="first-login-pwa-modal"
      >
        {/* Gradient hero strip */}
        <div className="relative bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-800 px-6 pt-7 pb-8 text-white">
          <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'radial-gradient(white 1px, transparent 1px)', backgroundSize: '18px 18px' }} aria-hidden="true" />
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Close"
            data-testid="first-login-pwa-close"
            className="absolute top-3 right-3 h-7 w-7 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/25 transition"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="relative flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center ring-1 ring-white/25 backdrop-blur">
              <Smartphone className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Out-Play · One-tap setup</p>
              <DialogTitle className="text-lg font-extrabold leading-tight mt-0.5">Get Out-Play on your phone</DialogTitle>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 bg-white dark:bg-slate-950">
          <DialogDescription className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            Skip the browser tabs — pin Out-Play to your home screen and get instant access to your training schedule, fee receipts and coach updates.
          </DialogDescription>

          {/* Benefit pills */}
          <div className="grid grid-cols-3 gap-2 text-center" data-testid="first-login-pwa-benefits">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/30 px-2 py-2.5">
              <BellRing className="h-4 w-4 mx-auto text-emerald-600 dark:text-emerald-400" />
              <p className="text-[10px] font-semibold mt-1 leading-tight">Instant lockscreen alerts</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-950/30 px-2 py-2.5">
              <Sparkles className="h-4 w-4 mx-auto text-amber-600 dark:text-amber-400" />
              <p className="text-[10px] font-semibold mt-1 leading-tight">Faster than the browser</p>
            </div>
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 dark:border-sky-900/40 dark:bg-sky-950/30 px-2 py-2.5">
              <Smartphone className="h-4 w-4 mx-auto text-sky-600 dark:text-sky-400" />
              <p className="text-[10px] font-semibold mt-1 leading-tight">Works offline-first</p>
            </div>
          </div>

          {/* iOS-specific guidance */}
          {isIos && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 p-3 text-xs leading-relaxed" data-testid="first-login-pwa-ios-instructions">
              <p className="font-bold text-slate-900 dark:text-slate-100 mb-1.5 flex items-center gap-1.5">
                <Share className="h-3.5 w-3.5" /> Safari users
              </p>
              <ol className="space-y-1 list-decimal pl-4 text-slate-600 dark:text-slate-400">
                <li>Tap the <strong>Share</strong> icon in Safari&apos;s bottom bar</li>
                <li>Scroll and pick <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong> — that&apos;s it!</li>
              </ol>
            </div>
          )}
        </div>

        <DialogFooter className="px-6 pb-5 gap-2 sm:gap-2 flex-col-reverse sm:flex-row bg-white dark:bg-slate-950">
          <Button
            type="button"
            variant="ghost"
            onClick={handleDismiss}
            className="w-full sm:w-auto text-slate-500"
            data-testid="first-login-pwa-skip"
          >
            Maybe later
          </Button>
          <Button
            type="button"
            onClick={handleInstall}
            disabled={busy}
            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 font-semibold"
            data-testid="first-login-pwa-install"
          >
            {isIos ? <Share className="h-4 w-4" /> : <Download className="h-4 w-4" />}
            {isIos ? 'Got it — show me how' : (busy ? 'Installing…' : 'Add to home screen')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Test helper — exported so backend pytest can verify the gate logic by
// importing the source string. Also handy for QA to manually reset the
// flag during a demo.
export function _resetFirstLoginPwaModalFlag() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* noop */ }
}
